import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Switch,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { videosAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Videos = () => {
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchVideos();
  }, [pagination.current, pagination.pageSize]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await videosAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize
      });
      if (response.data.success) {
        setVideos(response.data.data.videos);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取视频列表失败');
      console.error('Fetch videos error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingVideo(null);
    form.resetFields();
    form.setFieldsValue({ isPublished: true, isVip: false, order: 0 });
    setModalVisible(true);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    form.setFieldsValue(video);
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      // 确保 description 可以为空字符串，移除 thumbnail
      const submitData = {
        ...values,
        description: values.description || '',
        thumbnail: '' // 不再使用缩略图URL，前端直接使用视频预览
      };
      
      if (editingVideo) {
        await videosAPI.update(editingVideo.id, submitData);
        message.success('视频更新成功');
      } else {
        await videosAPI.create(submitData);
        message.success('视频创建成功');
      }
      
      setModalVisible(false);
      fetchVideos();
    } catch (error) {
      message.error('操作失败');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await videosAPI.delete(id);
      message.success('视频已删除');
      fetchVideos();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete error:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      render: (level) => {
        const colors = { Beginner: 'green', Intermediate: 'blue', Advanced: 'orange', Expert: 'red' };
        return <Tag color={colors[level]}>{level}</Tag>;
      },
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => {
        const labels = { basics: '基础', technical: '技术分析', fundamental: '基本面', strategy: '策略', psychology: '心理' };
        return labels[cat] || cat;
      },
    },
    {
      title: 'VIP',
      dataIndex: 'isVip',
      key: 'isVip',
      render: (isVip) => isVip ? <Tag color="gold">VIP</Tag> : <Tag>免费</Tag>,
    },
    {
      title: '播放量',
      dataIndex: 'views',
      key: 'views',
      render: (views) => views?.toLocaleString() || 0,
    },
    {
      title: '状态',
      dataIndex: 'isPublished',
      key: 'isPublished',
      render: (isPublished) => <Tag color={isPublished ? 'green' : 'default'}>{isPublished ? '已发布' : '草稿'}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>视频管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加视频
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={videos}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{ emptyText: '暂无视频数据' }}
        />
      </Card>

      <Modal
        title={editingVideo ? '编辑视频' : '添加视频'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="可选，填写视频描述" />
          </Form.Item>
          <Form.Item name="videoUrl" label="视频URL" rules={[{ required: true, message: '请输入视频URL' }]} extra="前端将直接使用视频内容作为预览缩略图">
            <Input placeholder="https://..." />
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item name="duration" label="时长" rules={[{ required: true, message: '请输入时长' }]}>
              <Input placeholder="12:34" style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="level" label="级别" rules={[{ required: true, message: '请选择级别' }]}>
              <Select style={{ width: 150 }}>
                <Option value="Beginner">Beginner</Option>
                <Option value="Intermediate">Intermediate</Option>
                <Option value="Advanced">Advanced</Option>
                <Option value="Expert">Expert</Option>
              </Select>
            </Form.Item>
            <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
              <Select style={{ width: 150 }}>
                <Option value="basics">基础</Option>
                <Option value="technical">技术分析</Option>
                <Option value="fundamental">基本面</Option>
                <Option value="strategy">策略</Option>
                <Option value="psychology">心理</Option>
              </Select>
            </Form.Item>
          </Space>
          <Space>
            <Form.Item name="isVip" label="VIP专属" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isPublished" label="发布状态" valuePropName="checked">
              <Switch checkedChildren="发布" unCheckedChildren="草稿" />
            </Form.Item>
            <Form.Item name="order" label="排序">
              <InputNumber min={0} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default Videos;
